import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';

export type AdminGroup = {
	groupName: string;
	description?: string;
};

export type AdminGroupUser = {
	userId: string;
	email?: string;
	username?: string;
};

export type AdminOperationResult = {
	success: boolean;
	errorMessage?: string;
};

@Injectable({ providedIn: 'root' })
export class AdminGroupsService {
	private readonly http = inject(HttpClient);

	getGroups(): Observable<AdminGroup[]> {
		return this.http
			.get<{ groups: AdminGroup[] }>(`${environment.api}/admin/groups`)
			.pipe(map(r => r.groups));
	}

	createGroup(groupName: string, description?: string): Observable<AdminOperationResult> {
		const body: { groupName: string; description?: string } = { groupName };
		if (description) body.description = description;
		return this.http
			.post<void>(`${environment.api}/admin/groups`, body, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 201 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}

	deleteGroup(groupName: string): Observable<AdminOperationResult> {
		return this.http
			.delete<void>(`${environment.api}/admin/groups/${groupName}`, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 204 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}

	getGroupUsers(groupName: string): Observable<AdminGroupUser[]> {
		return this.http
			.get<{ users: AdminGroupUser[] }>(`${environment.api}/admin/groups/${groupName}/users`)
			.pipe(map(r => r.users));
	}

	addUserToGroup(groupName: string, userId: string): Observable<AdminOperationResult> {
		return this.http
			.post<void>(`${environment.api}/admin/groups/${groupName}/users`, { userId }, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 201 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}

	removeUserFromGroup(groupName: string, userId: string): Observable<AdminOperationResult> {
		return this.http
			.delete<void>(`${environment.api}/admin/groups/${groupName}/users/${userId}`, { observe: 'response' })
			.pipe(
				map(r => ({ success: r.status === 200 || r.status === 204 })),
				catchError(err => of({ success: false, errorMessage: err.error?.message ?? err.message }))
			);
	}
}
